<template>
  <v-container
    v-if="editor"
    fluid
  >
    <v-row>
      <v-toolbar height="70">
        <v-col class="pt-5">
          <v-combobox
            v-model="defaultOption.font"
            :items="optionChoice.font"
            :label="$t('option.font')"
            hide-details
            @change="changeFont"
          >
            <template v-slot:item="{ active, item, attrs, on }">
              <v-list-item
                v-slot:default="{ active }"
                v-bind="attrs"
                :style="{ fontFamily: item }"
                v-on="on"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-combobox>
        </v-col>
        <v-col class="pt-5">
          <v-combobox
            v-model="defaultOption.size"
            :items="optionChoice.size"
            :label="$t('option.size')"
            suffix="%"
            hide-details
            @change="changeSize"
          />
        </v-col>
      </v-toolbar>
    </v-row>

    <v-row>
      <v-toolbar
        class="mt-3"
        height="70"
      >
        <v-col>
          <v-menu :close-on-content-click="false">
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                v-on="on"
              >
                <v-icon>mdi-palette</v-icon>
              </v-btn>
            </template>
            <v-color-picker
              mode="hexa"
              :value="defaultOption.color"
              @input="setColor"
            />
          </v-menu>
        </v-col>
        <v-col>
          <v-item-group class="v-btn-toggle">
            <v-btn @click="toggleBold()">
              <v-icon>mdi-format-bold</v-icon>
            </v-btn>
            <v-btn @click="toggleItalic()">
              <v-icon>mdi-format-italic</v-icon>
            </v-btn>
            <v-btn @click="toggleUnderline()">
              <v-icon>mdi-format-underline</v-icon>
            </v-btn>
          </v-item-group>
        </v-col>
      </v-toolbar>
    </v-row>

    <v-row class="mb-0">
      <v-toolbar
        class="mt-3"
        height="70"
      >
        <v-col>
          <v-item-group class="v-btn-toggle">
            <v-btn
              v-for="(align, index) in optionChoice.align"
              :key="index"
              :title="align.name"
              @click="setAlign(align.value)"
            >
              <v-icon>{{ "mdi-format-align-" + align.value }}</v-icon>
            </v-btn>
          </v-item-group>
        </v-col>
      </v-toolbar>
    </v-row>

    <v-row class="mt-3">
      <editor-content
        :editor="editor"
        style="width: 100%; height: 25vh"
      />
    </v-row>
  </v-container>
</template>

<script>
// Components
import { Editor, EditorContent } from "@tiptap/vue-2";

// Editor extensions
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import TextStyle from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import TextAlign from "@tiptap/extension-text-align";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Underline from "@tiptap/extension-underline";
import Color from "@tiptap/extension-color";
import Placeholder from "@tiptap/extension-placeholder";
import FontSize from "tiptap-extension-font-size";

export default {
  components: {
    EditorContent,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      editor: null,
      defaultOption: {
        align: "left",
        color: "#fffffff",
        font: this.$store.state.vars.font.default,
        size: 100,
      },
      optionChoice: {
        align: [
          { name: this.$t("align.left"), value: "left" },
          { name: this.$t("align.center"), value: "center" },
          { name: this.$t("align.right"), value: "right" },
          { name: this.$t("align.justify"), value: "justify" },
        ],
        font: this.$store.state.vars.font.family,
        size: [25, 50, 75, 100, 125, 150, 175, 200],
      },
    };
  },
  watch: {
    value(value) {
      const isSame = this.editor.getHTML() === value;

      if (isSame) {
        return;
      }

      // Reset default values
      this.defaultOption = {
        align: "left",
        color: "#fffffff",
        font: "Roboto",
        size: 100,
      };

      this.editor.commands.setContent(value, false);
    },
  },
  mounted() {
    this.initEditor();
  },
  created() {
    this.$nextTick(function () {
      this.setAlign(this.defaultOption.align);
      this.changeFont(this.defaultOption.font);
      this.changeSize(this.defaultOption.size);
      this.setColor(this.defaultOption.color);
    });
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  methods: {
    initEditor() {
      this.editor = new Editor({
        extensions: [
          Document,
          Paragraph,
          Text,
          TextStyle,
          FontFamily,
          FontSize,
          Bold,
          Italic,
          Underline,
          Color,
          TextAlign.configure({
            types: ["paragraph"],
          }),
          Placeholder.configure({
            placeholder: this.$t("creation.tools.text-here"),
          }),
        ],
        onUpdate: () => {
          this.$emit("update:value", this.editor.getHTML());
        },
      });
    },
    changeFont(font) {
      this.editor.chain().focus().setFontFamily(font).run();
    },
    changeSize(size) {
      if (isNaN(size.toString())) {
        size = 100;
      }

      const fontsize = size.toString() + "%";
      this.editor.chain().focus().setFontSize(fontsize).run();
    },
    setAlign(val) {
      this.editor.chain().focus().setTextAlign(val).run();
    },
    setColor(color) {
      this.editor.chain().focus().setColor(color).run();
    },
    toggleBold() {
      this.editor.chain().focus().toggleBold().run();
    },
    toggleItalic() {
      this.editor.chain().focus().toggleItalic().run();
    },
    toggleUnderline() {
      this.editor.chain().focus().toggleUnderline().run();
    },
  },
};
</script>

<style lang="scss">
/* Basic editor styles */
.ProseMirror {
  > * + * {
    margin-top: 0.75em;
  }

  height: 100%;
  padding: 10px;
  border: 1px solid #ffffff11;
  color: #ffffffff;

  overflow: auto;
}

.ProseMirror:hover {
  cursor: text;
}

.ProseMirror:focus {
  outline: none;
}

/* Placeholder (at the top) */
.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  height: 0;
  color: #adb5bd;
  pointer-events: none;
}

/**
    Button group
  */
.v-btn-toggle {
  border-radius: 4px;
  display: -webkit-inline-box;
  display: -ms-inline-flexbox;
  display: inline-flex;
  max-width: 100%;
}

.v-btn-toggle:not(.v-btn-toggle--dense) .v-btn.v-btn.v-size--default {
  height: 48px;
}

.v-application--is-ltr .v-btn-toggle > .v-btn.v-btn:first-child {
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
}

.v-application--is-ltr .v-btn-toggle > .v-btn.v-btn:last-child {
  border-top-right-radius: inherit;
  border-bottom-right-radius: inherit;
}

.theme--dark.v-btn-toggle:not(.v-btn-toggle--group) .v-btn.v-btn {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.v-btn-toggle .v-btn.v-btn.v-size--default {
  min-width: 48px;
  min-height: 0;
}

.v-btn-toggle > .v-btn.v-btn {
  max-width: 48px;

  padding: 0 12px;

  border-radius: 0;
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  border-style: solid;
  border-width: thin;

  -webkit-box-shadow: none;
  box-shadow: none;
  box-shadow: none;
  opacity: 0.8;
}
</style>
